import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, Note, PaginatedApiResponse } from '@card-software/library';
import {
  AvailableLotItemDto,
  LotItemTransferDto,
  LotItemTransferInsertCommand,
  LotItemTransferSearchCommand,
  BatchAddLotItemKeyCommand,
  LotItemTransferStatusCommand,
} from '../../models/inventory/lot-item-transfer';
import { EntityKey } from '../../models/inventory/keys/entity-key';
import { InventoryTransaction } from '../../models/inventory/inventory-transaction';

@Injectable({
  providedIn: 'root',
})
export class LotItemTransferProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/lot-item-transfer';

  /**
   * Get transfer by ID
   */
  public getById(id: string): Observable<ApiResponse<LotItemTransferDto>> {
    return this.httpClient.get<ApiResponse<LotItemTransferDto>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Search transfers with pagination
   */
  public search(
    searchCommand: LotItemTransferSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<LotItemTransferDto>>> {
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      queryParams += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<LotItemTransferDto>>>(
      this.baseUrl + '/search' + queryParams,
      searchCommand,
    );
  }

  /**
   * Create new transfer (Draft status)
   */
  public create(command: LotItemTransferInsertCommand): Observable<ApiResponse<LotItemTransferDto>> {
    return this.httpClient.post<ApiResponse<LotItemTransferDto>>(this.baseUrl, command);
  }

  /**
   * Batch add items to transfer
   */
  public batchAddItems(id: string, command: BatchAddLotItemKeyCommand): Observable<ApiResponse<LotItemTransferDto>> {
    return this.httpClient.post<ApiResponse<LotItemTransferDto>>(`${this.baseUrl}/${id}/items/batch-add`, command);
  }

  /**
   * Remove item from transfer
   */
  public removeItem(id: string, itemNumber: string, entityDefinitionCode: number): Observable<ApiResponse<LotItemTransferDto>> {
    return this.httpClient.delete<ApiResponse<LotItemTransferDto>>(
      `${this.baseUrl}/${id}/items/${encodeURIComponent(itemNumber)}?entityDefinitionCode=${entityDefinitionCode}`,
    );
  }

  /**
   * Update transfer status
   */
  public updateStatus(id: string, command: LotItemTransferStatusCommand): Observable<ApiResponse<void>> {
    return this.httpClient.patch<ApiResponse<void>>(`${this.baseUrl}/${id}/status`, command);
  }

  /**
   * Update transfer notes
   */
  public updateNotes(id: string, notes: Note[]): Observable<ApiResponse<LotItemTransferDto>> {
    return this.httpClient.patch<ApiResponse<LotItemTransferDto>>(`${this.baseUrl}/${id}/notes`, notes);
  }

  /**
   * Update transfer initiator (e.g. Batch reference for TransferToFinishedGoods)
   */
  public updateInitiator(id: string, initiator: EntityKey): Observable<ApiResponse<LotItemTransferDto>> {
    return this.httpClient.patch<ApiResponse<LotItemTransferDto>>(`${this.baseUrl}/${id}/initiator`, initiator);
  }

  /**
   * Update transfer locations (Draft status only, no items)
   */
  public updateLocations(id: string, fromLocationCode: string, toLocationCode: string): Observable<ApiResponse<LotItemTransferDto>> {
    return this.httpClient.patch<ApiResponse<LotItemTransferDto>>(`${this.baseUrl}/${id}/locations`, {
      fromLocationCode,
      toLocationCode,
    });
  }

  /**
   * Get linked inventory transaction
   */
  public getTransaction(id: string): Observable<ApiResponse<InventoryTransaction | null>> {
    return this.httpClient.get<ApiResponse<InventoryTransaction | null>>(`${this.baseUrl}/${id}/transaction`);
  }

  /**
   * Delete transfer (Draft only)
   */
  public delete(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Get available lot items (yarn) at locations matching the given prefix.
   * Excludes items already in active transfers.
   */
  public getAvailableLotItems(
    locationPrefix: string,
    pageNumber = 1,
    pageSize = 25,
    searchTerm?: string,
    itemCode?: string,
    manufacturerName?: string,
    providerName?: string,
    manufacturerLotNumber?: string,
  ): Observable<ApiResponse<PaginatedApiResponse<AvailableLotItemDto>>> {
    let params = `?locationPrefix=${encodeURIComponent(locationPrefix)}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (searchTerm) params += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (itemCode) params += `&itemCode=${encodeURIComponent(itemCode)}`;
    if (manufacturerName) params += `&manufacturerName=${encodeURIComponent(manufacturerName)}`;
    if (providerName) params += `&providerName=${encodeURIComponent(providerName)}`;
    if (manufacturerLotNumber) params += `&manufacturerLotNumber=${encodeURIComponent(manufacturerLotNumber)}`;
    return this.httpClient.get<ApiResponse<PaginatedApiResponse<AvailableLotItemDto>>>(`${this.baseUrl}/available-lot-items${params}`);
  }
}
